import React, { useState } from 'react';
import { X, DollarSign, SkipForward } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls
const API = '/api';

const BudgetOnboarding = ({ isOpen, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState('');

  const handleSetBudget = async () => {
    const amount = parseFloat(budgetAmount.replace(/[^0-9.]/g, ''));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid budget amount');
      return;
    }

    setLoading(true);
    try {
      // Try to get existing wedding plan
      let planExists = false;
      try {
        await axios.get(`${API}/wedding-plans/${userId}`);
        planExists = true;
      } catch (err) {
        planExists = false;
      }

      if (planExists) {
        // Update existing plan
        await axios.put(`${API}/wedding-plans/${userId}`, {
          total_budget: amount
        });
      } else {
        // Create new plan with budget
        await axios.post(`${API}/wedding-plans`, {
          wedding_date: new Date().toISOString().split('T')[0],
          total_budget: amount
        }, {
          headers: { 'user-id': userId }
        });
      }
      
      toast.success('Budget set! 🎉');
      onClose(true, false); // completed=true, skipped=false
    } catch (error) {
      console.error('Budget error:', error);
      toast.error(error.response?.data?.detail || 'Failed to set budget');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast.success('You can set your budget anytime from the Budget page');
    onClose(false, true); // completed=false, skipped=true
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-stone-200 bg-gradient-to-r from-[#881337] to-[#9F1239]">
          <div className="flex items-center gap-3">
            <DollarSign className="text-white" size={28} />
            <h2 className="text-2xl font-serif font-bold text-white">Set Your Budget</h2>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-stone-50 rounded-xl p-6 border-2 border-dashed border-stone-300">
            <h3 className="text-lg font-semibold text-[#1C1917] mb-3">Plan Your Wedding Finances</h3>
            <p className="text-sm text-stone-700 mb-3">
              Setting a budget helps you:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-stone-700 ml-4">
              <li>Track spending across all vendors</li>
              <li>Get AI recommendations within your range</li>
              <li>Avoid overspending surprises</li>
              <li>Make informed decisions</li>
            </ul>
          </div>

          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              Total Wedding Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-500 text-lg">$</span>
              <Input
                type="text"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                placeholder="e.g., 25000"
                className="border-stone-300 pl-8 text-lg"
              />
            </div>
            <p className="text-xs text-stone-500 mt-1">
              💡 Include everything: venue, catering, photography, attire, etc.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>Not sure yet?</strong> No problem! You can always come back and set this later. Click "Skip for Now" to continue.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSkip}
              className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700 flex items-center justify-center gap-2"
            >
              <SkipForward size={18} />
              Skip for Now
            </Button>
            <Button
              onClick={handleSetBudget}
              disabled={loading || !budgetAmount.trim()}
              className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              {loading ? 'Setting...' : 'Set Budget'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetOnboarding;